import { useEffect, lazy, Suspense } from 'react'
import { Routes, Route, Navigate, Outlet } from 'react-router-dom'
import useAuthStore from './stores/authStore'
import Layout from './components/layout/Layout'
import Loading from './components/common/Loading'
import RequireModule from './components/auth/RequireModule'

// Flag global para evitar múltiples llamadas a checkAuth
let authCheckInitiated = false

// Auth Pages - No lazy load (críticas para primera carga)
import Login from './pages/Login'
import Register from './pages/Register'

// Lazy loaded pages para mejor performance
const Dashboard = lazy(() => import('./pages/Dashboard'))
const Productos = lazy(() => import('./pages/Productos'))

// Clientes
const ClientesPage = lazy(() => import('./pages/clientes/ClientesPage'))
const ClienteFormPage = lazy(() => import('./pages/clientes/ClienteFormPage'))

// Cotizaciones
const CotizacionPage = lazy(() => import('./pages/cotizaciones/CotizacionPage'))
const CotizacionesGuardadasPage = lazy(() => import('./pages/cotizaciones/CotizacionesGuardadasPage'))
const CotizacionesPorClientePage = lazy(() => import('./pages/cotizaciones/CotizacionesPorClientePage'))
const CotizacionesPorMarcaPage = lazy(() => import('./pages/cotizaciones/CotizacionesPorMarcaPage'))
const CotizacionDetallePage = lazy(() => import('./pages/cotizaciones/CotizacionDetallePage'))

// Reportes
const ReportesIndexPage = lazy(() => import('./pages/reportes/ReportesIndexPage'))
const ItemsMasCotizadosPage = lazy(() => import('./pages/reportes/ItemsMasCotizadosPage'))
const ValorPorClientePage = lazy(() => import('./pages/reportes/ValorPorClientePage'))
const TopMarcasPage = lazy(() => import('./pages/reportes/TopMarcasPage'))
const ConsultaIAPage = lazy(() => import('./pages/reportes/ConsultaIAPage'))

// Usuarios
const UsuariosPage = lazy(() => import('./pages/usuarios/UsuariosPage'))
const UsuarioFormPage = lazy(() => import('./pages/usuarios/UsuarioFormPage'))

// Admin
const MantenimientoPage = lazy(() => import('./pages/admin/MantenimientoPage'))  // Issue #36 - Módulo unificado
const ProveedoresPage = lazy(() => import('./pages/admin/ProveedoresPage'))
const ProveedoresLegacyPage = lazy(() => import('./pages/admin/ProveedoresLegacyPage'))
const ProveedorFormPage = lazy(() => import('./pages/admin/ProveedorFormPage'))
const ProveedorProductosPage = lazy(() => import('./pages/admin/ProveedorProductosPage'))
const PermisosPage = lazy(() => import('./pages/admin/PermisosPage'))
const TransportistasPage = lazy(() => import('./pages/admin/TransportistasPage'))
const MercanciaLegacyPage = lazy(() => import('./pages/admin/MercanciaLegacyPage'))
const HistoricoLegacyPage = lazy(() => import('./pages/clientes/HistoricoLegacyPage'))
const FacturacionPage = lazy(() => import('./pages/admin/FacturacionPage'))
const PreciosEscalonadosPage = lazy(() => import('./pages/admin/PreciosEscalonadosPage'))  // Issue #21.2

// Cubiertas (Mayo 2026)
const CubiertasPage = lazy(() => import('./pages/cubiertas/CubiertasPage'))

// Pedidos
const PedidosPage = lazy(() => import('./pages/pedidos/PedidosPage'))
const PedidoDetallePage = lazy(() => import('./pages/pedidos/PedidoDetallePage'))
const PedidosPorClientePage = lazy(() => import('./pages/pedidos/PedidosPorClientePage'))
const PedidosGuardadosPage = lazy(() => import('./pages/pedidos/PedidosGuardadosPage'))  // Issue #31.2

// Catálogo FMSI
const CatalogoFmsiPage = lazy(() => import('./pages/catalogo-fmsi/CatalogoFmsiPage'))
const ProductoFmsiDetallePage = lazy(() => import('./pages/catalogo-fmsi/ProductoFmsiDetallePage'))
const ProductoIcerDetallePage = lazy(() => import('./pages/catalogo-fmsi/ProductoIcerDetallePage'))

// Other
const Perfil = lazy(() => import('./pages/Perfil'))
const NotFound = lazy(() => import('./pages/NotFound'))

// Suspense wrapper con Loading
function PageLoader({ children }: { children: React.ReactNode }) {
  return (
    <Suspense fallback={<Loading fullScreen message="Cargando..." />}>
      {children}
    </Suspense>
  )
}

// Wrapper que combina PageLoader + RequireModule para mantener las rutas legibles
function M({ modulo, accion = 'ver', children }: { modulo: string; accion?: string; children: React.ReactNode }) {
  return (
    <RequireModule modulo={modulo} accion={accion}>
      <PageLoader>{children}</PageLoader>
    </RequireModule>
  )
}

// Protected Route Component
function ProtectedRoute() {
  const { isAuthenticated, isLoading, user } = useAuthStore()

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }

  // Issue #14: Pasar user al Layout para mostrar menús según rol
  const layoutUser = user ? {
    name: user.nombre || user.username,
    email: user.email || '',
    role: user.rol
  } : undefined

  return (
    <Layout user={layoutUser}>
      <Outlet />
    </Layout>
  )
}

// Public Route - Redirect if authenticated
function PublicRoute() {
  const { isAuthenticated, isLoading } = useAuthStore()

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />
  }

  return <Outlet />
}

function App() {
  // Verificar autenticación al cargar la app - solo una vez
  useEffect(() => {
    // Flag global para garantizar que solo se llame UNA vez
    // incluso con React StrictMode o re-renders
    if (!authCheckInitiated) {
      authCheckInitiated = true
      useAuthStore.getState().checkAuth()
    }
  }, [])

  return (
    <Routes>
        {/* Public routes */}
        <Route element={<PublicRoute />}>
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
        </Route>

        {/* Protected routes */}
        <Route element={<ProtectedRoute />}>
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route path="/dashboard" element={<M modulo="dashboard"><Dashboard /></M>} />
          <Route path="/productos" element={<M modulo="productos"><Productos /></M>} />

          {/* Clientes */}
          <Route path="/clientes" element={<M modulo="clientes"><ClientesPage /></M>} />
          <Route path="/clientes/nuevo" element={<M modulo="clientes"><ClienteFormPage /></M>} />
          <Route path="/clientes/:id/editar" element={<M modulo="clientes"><ClienteFormPage /></M>} />
          <Route path="/clientes/:clienteId/historico-legacy" element={<M modulo="clientes"><HistoricoLegacyPage /></M>} />

          {/* Cotizaciones */}
          <Route path="/cotizaciones" element={<M modulo="cotizaciones"><CotizacionesGuardadasPage /></M>} />
          <Route path="/cotizaciones/nueva" element={<M modulo="cotizaciones_nueva"><CotizacionPage /></M>} />
          <Route path="/cotizaciones/:id" element={<M modulo="cotizaciones"><CotizacionPage /></M>} />
          <Route path="/cotizaciones/detalle/:quoteId" element={<M modulo="cotizaciones"><CotizacionDetallePage /></M>} />
          <Route path="/cotizaciones/cliente/:clienteId" element={<M modulo="cotizaciones"><CotizacionesPorClientePage /></M>} />
          <Route path="/cotizaciones/cliente/:clienteId/marca/:proveedorCode" element={<M modulo="cotizaciones"><CotizacionesPorMarcaPage /></M>} />

          {/* Reportes */}
          <Route path="/reportes" element={<M modulo="reportes"><ReportesIndexPage /></M>} />
          <Route path="/reportes/items-mas-cotizados" element={<M modulo="reportes"><ItemsMasCotizadosPage /></M>} />
          <Route path="/reportes/valor-por-cliente" element={<M modulo="reportes"><ValorPorClientePage /></M>} />
          <Route path="/reportes/top-marcas" element={<M modulo="reportes"><TopMarcasPage /></M>} />
          <Route path="/reportes/consulta-ia" element={<M modulo="reportes"><ConsultaIAPage /></M>} />

          {/* Usuarios — solo admin (módulo del grupo admin) */}
          <Route path="/usuarios" element={<M modulo="usuarios"><UsuariosPage /></M>} />
          <Route path="/usuarios/nuevo" element={<M modulo="usuarios"><UsuarioFormPage /></M>} />
          <Route path="/usuarios/:id/editar" element={<M modulo="usuarios"><UsuarioFormPage /></M>} />

          {/* Cubiertas (gating cubierta→procesar) */}
          <Route path="/cubiertas" element={<M modulo="cubiertas"><CubiertasPage /></M>} />

          {/* Pedidos */}
          {/* /pedidos redirige a la vista organizada por cliente (mismo patrón que cotizaciones) */}
          <Route path="/pedidos" element={<Navigate to="/pedidos-guardados" replace />} />
          <Route path="/pedidos/lista-plana" element={<M modulo="pedidos"><PedidosPage /></M>} />
          <Route path="/pedidos-guardados" element={<M modulo="pedidos"><PedidosGuardadosPage /></M>} />
          <Route path="/pedidos/cliente/:clienteId" element={<M modulo="pedidos"><PedidosPorClientePage /></M>} />
          <Route path="/pedidos/:id" element={<M modulo="pedidos"><PedidoDetallePage /></M>} />

          {/* Catálogo FMSI */}
          <Route path="/catalogo-fmsi" element={<M modulo="catalogo_fmsi"><CatalogoFmsiPage /></M>} />
          <Route path="/catalogo-fmsi/:partId" element={<M modulo="catalogo_fmsi"><ProductoFmsiDetallePage /></M>} />
          <Route path="/catalogo-icer/:productoId" element={<M modulo="catalogo_fmsi"><ProductoIcerDetallePage /></M>} />

          {/* Admin */}
          <Route path="/admin/mantenimiento" element={<M modulo="mantenimiento"><MantenimientoPage /></M>} />
          <Route path="/admin/precios-escalonados" element={<M modulo="mantenimiento"><PreciosEscalonadosPage /></M>} />
          <Route path="/admin/proveedores" element={<M modulo="mantenimiento"><ProveedoresPage /></M>} />
          <Route path="/admin/proveedores/nuevo" element={<M modulo="mantenimiento"><ProveedorFormPage /></M>} />
          <Route path="/admin/proveedores/:id/editar" element={<M modulo="mantenimiento"><ProveedorFormPage /></M>} />
          <Route path="/admin/proveedores/:id/productos" element={<M modulo="mantenimiento"><ProveedorProductosPage /></M>} />
          <Route path="/admin/proveedores-legacy" element={<M modulo="proveedores_legacy"><ProveedoresLegacyPage /></M>} />
          <Route path="/admin/permisos" element={<M modulo="permisos"><PermisosPage /></M>} />
          <Route path="/admin/transportistas" element={<M modulo="mantenimiento"><TransportistasPage /></M>} />
          <Route path="/admin/mercancia-legacy" element={<M modulo="mantenimiento"><MercanciaLegacyPage /></M>} />
          <Route path="/admin/facturacion" element={<M modulo="pedidos"><FacturacionPage /></M>} />

          {/* Catalogo - Issue #36: Redirecciones a módulo unificado */}
          <Route path="/catalogo" element={<Navigate to="/admin/mantenimiento?tab=global" replace />} />
          <Route path="/admin/precios" element={<Navigate to="/admin/mantenimiento?tab=proveedor" replace />} />

          {/* Perfil */}
          <Route path="/perfil" element={<PageLoader><Perfil /></PageLoader>} />
        </Route>

        {/* 404 */}
        <Route path="*" element={<PageLoader><NotFound /></PageLoader>} />
      </Routes>
  )
}

export default App
